import React, { useRef } from "react";
import { graphql } from "gatsby";
import { useIntl } from "gatsby-plugin-intl";
import { DynamicTitle } from "@components/Title";
import { StaticImage } from "gatsby-plugin-image";
import TabNav from "@components/TabNav";
import SideNav from "@components/SideNav";
import Hero from "@components/Hero";
import ContentSection from "@components/ContentSection";
import NextTrigger from "@components/NextTrigger";
import { mq, filterLocale } from "../util";

const HowToPlayPage = ({ data }) => {
  const { formatMessage, locale } = useIntl();
  const rhythmModeRef = useRef(null);
  const storyModeRef = useRef(null);
  const stayTunedRef = useRef(null);

  return (
    <>
      <div
        css={mq({
          display: "grid",
          height: [486, 781],
        })}>
        <StaticImage
          src="../assets/images/global/billboard.png"
          alt="Station"
          layout="fullWidth"
          css={{
            gridArea: "1/1",
            zIndex: -1,
          }}
        />
        <div
          css={mq({
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gridArea: "1/1",
            paddingTop: [76, 137],
          })}>
          <DynamicTitle page="howToPlay">{formatMessage({ id: "nav-how-to-play" })}</DynamicTitle>
          <TabNav
            items={[
              {
                icon: "note",
                target: rhythmModeRef,
                text: formatMessage({ id: "how-to-play-title-rhythm-mode" }),
              },
              {
                icon: "doc",
                target: storyModeRef,
                text: formatMessage({ id: "how-to-play-title-story-mode" }),
              },
              {
                icon: "glory",
                target: stayTunedRef,
                text: formatMessage({ id: "how-to-play-title-stay-tuned" }),
              },
            ]}
          />
        </div>
      </div>
      <div ref={rhythmModeRef}>
        <Hero
          file={data.bgRhythmMode}
          alt="Rhythm mode"
          title={formatMessage({ id: "how-to-play-title-rhythm-mode" })}
          content={formatMessage({ id: "how-to-play-content-rhythm-mode" })}
        />
        {filterLocale(data.rhythmModeArticles?.nodes, locale).map((node, i) => (
          <ContentSection key={node.frontmatter.title} {...node} />
        ))}
      </div>
      <NextTrigger target={storyModeRef} />
      <div ref={storyModeRef}>
        <Hero
          file={data.bgStoryMode}
          alt="Story mode"
          title={formatMessage({ id: "how-to-play-title-story-mode" })}
          content={formatMessage({ id: "how-to-play-content-story-mode" })}
        />
        {filterLocale(data.storyModeArticles?.nodes, locale).map((node, i) => (
          <ContentSection key={node.frontmatter.title} {...node} />
        ))}
      </div>
      <NextTrigger target={stayTunedRef} />
      <div ref={stayTunedRef}>
        <Hero
          file={data.bgStayTuned}
          alt="Stay tuned"
          title={formatMessage({ id: "how-to-play-title-stay-tuned" })}
          content={formatMessage({ id: "how-to-play-content-stay-tuned" })}
        />
        {filterLocale(data.stayTunedArticles?.nodes, locale).map((node, i) => (
          <ContentSection key={node.frontmatter.title} {...node} />
        ))}
      </div>
      <SideNav
        items={[
          {
            target: rhythmModeRef,
            text: formatMessage({ id: "how-to-play-title-rhythm-mode" }),
          },
          {
            target: storyModeRef,
            text: formatMessage({ id: "how-to-play-title-story-mode" }),
          },
          { target: stayTunedRef, text: formatMessage({ id: "how-to-play-title-stay-tuned" }) },
        ]}
      />
    </>
  );
};

export const heroImageFragment = graphql`
  fragment heroImage on File {
    childImageSharp {
      gatsbyImageData(width: 1920, placeholder: BLURRED, formats: [AUTO, WEBP], layout: FULL_WIDTH)
    }
  }
`;

export const contentFragment = graphql`
  fragment howToPlayContent on MarkdownRemark {
    html
    frontmatter {
      title
      image
    }
    fileAbsolutePath
  }
`;

export const pageQuery = graphql`
  query {
    bgRhythmMode: file(relativePath: { eq: "how-to-play/bg-rhythm-mode.png" }) {
      ...heroImage
    }
    bgStoryMode: file(relativePath: { eq: "how-to-play/bg-story-mode.png" }) {
      ...heroImage
    }
    bgStayTuned: file(relativePath: { eq: "how-to-play/bg-stay-tuned.png" }) {
      ...heroImage
    }
    rhythmModeArticles: allMarkdownRemark(
      filter: { frontmatter: { category: { eq: "how_to_play_rhythm_mode" } } }
      sort: { order: ASC, fields: frontmatter___order }
    ) {
      nodes {
        ...howToPlayContent
      }
    }
    storyModeArticles: allMarkdownRemark(
      filter: { frontmatter: { category: { eq: "how_to_play_story_mode" } } }
      sort: { order: ASC, fields: frontmatter___order }
    ) {
      nodes {
        ...howToPlayContent
      }
    }
    stayTunedArticles: allMarkdownRemark(
      filter: { frontmatter: { category: { eq: "how_to_play_stay_tuned" } } }
      sort: { order: ASC, fields: frontmatter___order }
    ) {
      nodes {
        ...howToPlayContent
      }
    }
  }
`;

export default HowToPlayPage;
